import { Box, Stack, Typography } from '@mui/material';
import { ChangeEvent, FunctionComponent, useMemo } from 'react';
import { useGetProductPersonNameConfigurationQuery } from '@/api/v2/textReplacement';
import { useAuthentication } from '@/hooks/useAuthentication';
import { Select } from '@/components/Select/Select';
import { ConditionLinkPersonType } from '@/models/conditionLink';

export type PersonTypeMap = {
    [personIdToMap: string]: string;
};

type ConditionLinkPersonTypeMappingProps = {
    personsToMap: {
        [importedPersonId: string]: string;
    };
    onMappingChange: (mapping: { [personIdToMap: string]: string }) => void;
    personTypes: ConditionLinkPersonType[];
    showMissingWithError?: boolean;
};

export const ConditionLinkPersonTypeMapping: FunctionComponent<
    ConditionLinkPersonTypeMappingProps
> = ({
    personsToMap,
    onMappingChange,
    personTypes,
    showMissingWithError = false,
}) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { data: personsMap } =
        useGetProductPersonNameConfigurationQuery(liftOperatorId);

    const personTypeSelectValues = useMemo(() => {
        if (personsMap?.persons) {
            return personsMap.persons.map((person) => ({
                value: person.id,
                label: person.name,
            }));
        }
        return [];
    }, [personsMap]);

    const onPersonTypeSelect =
        (idToMap: string) => (e: ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;

            onMappingChange({
                ...personsToMap,
                [idToMap]: value === '-' ? undefined : value,
            });
        };

    return personsMap ? (
        <Box>
            <Typography variant='subtitle1' sx={{ mb: 2 }}>
                Personentypen zuordnen
            </Typography>

            <Typography
                sx={{
                    mb: 4,
                }}
            >
                Bitte die Personentypen des importierten Regelwerks den eigenen
                Personentypen zuordnen.
            </Typography>

            <Stack spacing={2}>
                {Object.keys(personsToMap).map((personId) => {
                    const personType = personTypes.find(
                        (person) => person.id === personId
                    );

                    if (!personType) return null;

                    const isMissing = !personsToMap[personId];

                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                            }}
                            key={personId}
                        >
                            <Typography
                                sx={{
                                    width: 240,
                                }}
                            >
                                {personType.name.de}:
                            </Typography>

                            <Select
                                sx={{
                                    width: 400,
                                }}
                                error={isMissing && showMissingWithError}
                                helperText={
                                    isMissing && showMissingWithError
                                        ? 'Bitte hier einen Personentyp angeben'
                                        : ''
                                }
                                value={personsToMap[personId] || '-'}
                                onChange={onPersonTypeSelect(personId)}
                                label='Personentyp auswählen'
                            >
                                <option>-</option>
                                {personTypeSelectValues.map(
                                    ({ value, label }) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </Select>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    ) : null;
};
